import { calcAmountToLeverage, calcMarginToAmount } from '@flipster/utils/calculator/order-preview';
import Num from '@flipster/utils/num';

import type { OrderState } from '../../form';

/**
 * 오더폼 leverage 입력 검증 결과.
 * 메시지(i18n) 매핑은 소비 측(validationBunja)에서 하고, 여기선 "어떤 에러인지"만 판정한다
 * — 순수 로직이라 bunja/i18n 없이 단독 unit test 가능하다(leverage-error.test.ts).
 */
export type LeverageError =
  | { type: 'minInput' }
  | { type: 'exceedsMaxLeverage'; maxLeverage: number }
  | { type: 'belowPositionLeverage' }
  | { type: 'exceedsNotionalMax'; maxLeverage: number };

export interface LeverageErrorInput {
  leverage: number;
  kind: OrderState['kind'];
  /** MarginState(kind==='margin')에서만 존재 */
  margin?: string;
  /** Mode-aware ceiling (Isolated → effectiveIsolatedMax, Cross → crossMax) */
  maxLeverage: number;
  notionalMaxOrderAmount: string;
  /** one-way 모드에서 열린 포지션(또는 slot 0 pending)이 있는지 */
  hasOneWayPosition: boolean;
  /** one-way 포지션의 leverage (없으면 null/undefined) */
  positionLeverage: number | null | undefined;
  reduceOnly: boolean;
}

/**
 * 오더폼 leverage 입력 검증 (순수 로직).
 *
 * reduce-only는 신규 리스크를 늘리지 않으므로 "상한(신규 진입 제한)" 계열 검증을 스킵한다
 * — leverage 하드리밋 초과 / notional 상한 초과. (reduce-only는 서버가 대신 검증)
 * 예: 200x 포지션 보유 중 position bracket max가 50x로 축소돼도 reduce-only 주문은 허용돼야 한다.
 */
export const getLeverageError = ({
  leverage,
  kind,
  margin,
  maxLeverage,
  notionalMaxOrderAmount,
  hasOneWayPosition,
  positionLeverage,
  reduceOnly,
}: LeverageErrorInput): LeverageError | null => {
  if (!leverage || Number(leverage) === 0) return { type: 'minInput' };

  if (!reduceOnly && leverage > maxLeverage) return { type: 'exceedsMaxLeverage', maxLeverage };

  if (hasOneWayPosition && positionLeverage != null && leverage < positionLeverage)
    return { type: 'belowPositionLeverage' };

  if (
    !reduceOnly &&
    kind === 'margin' &&
    Num(calcMarginToAmount(margin, leverage)).isGreaterThan(notionalMaxOrderAmount)
  ) {
    const maxLeverage = Math.max(calcAmountToLeverage(notionalMaxOrderAmount, margin), 1);
    return { type: 'exceedsNotionalMax', maxLeverage };
  }

  return null;
};
